import { searchCorpora, WHOLE_TEXT, type AttachedFilter, type CorpusSearchHit, type CorpusSearchScope } from "../../../api/content/searchApi";
import { findLatestDocumentVersion, readSourceDocument } from "../../../api/content/sourcesApi";
import { isProblemWithCode, toUiError } from "../../../errors/uiError";
import { parseContentAddress, SEGMENT_SEPARATOR, VERSION_SEPARATOR, type ContentAddress, type DocumentAddress } from "../../../shared/references/contentReferences";

/** What the search covers: an address is found where its text would be. */
export interface SearchedDocuments {
  readonly scope: CorpusSearchScope;
  readonly attached: AttachedFilter;
}

/** A document, or a version or segment of one, that the search field's text names. */
export interface NamedAddress {
  readonly address: ContentAddress;
  /** The document's key, to show in place of its ID (P-F). */
  readonly documentKey: string;
  /** The document as the search found it by its key; `null` for one named by its reference. */
  readonly hit: CorpusSearchHit | null;
}

/**
 * The document, version or segment `written` names (F135, TR-B4-03), else `null`, so the field
 * searches it as text. Only text with a version (`@`) or a segment (`#`) may name one; search
 * syntax uses neither, so plain text and a plain key are left to the search, which finds a key
 * first. With one of them:
 * - the whole text as a document's key, in full or a short form its corpus declares (a key may
 *   hold `#` or `@`, Sol R3-1);
 * - else the part before the first `@` or `#` as a key (`3 / 58#head`), with the version and
 *   segment after it;
 * - else the text as a reference, `ID@version` or `ID#segment`, once its document is read.
 * Keys are looked up through the one search, which owns the corpora's key forms, at most twice.
 */
export async function findNamedAddress(projectId: string, written: string, searched: SearchedDocuments, signal?: AbortSignal): Promise<NamedAddress | null> {
  const separators = [written.indexOf(VERSION_SEPARATOR), written.indexOf(SEGMENT_SEPARATOR)].filter((index) => index !== -1);
  if (separators.length === 0) return null;
  const cut = Math.min(...separators);
  const whole = await findDocumentByKey(projectId, written, searched, signal);
  if (whole !== null) return { address: { document: { documentId: whole.document.documentId, version: null }, segmentPath: null }, documentKey: whole.documentKey, hit: whole };
  const documentPart = written.slice(0, cut).trim();
  const keyed = documentPart === "" ? null : await findDocumentByKey(projectId, documentPart, searched, signal);
  if (keyed !== null) {
    const address = parseContentAddress(`${keyed.document.documentId}${written.slice(cut).trim()}`);
    return address === null ? null : { address, documentKey: keyed.documentKey, hit: keyed };
  }
  const reference = parseContentAddress(written);
  if (reference === null) return null;
  const documentKey = await readDocumentKey(reference.document, signal);
  return documentKey === null ? null : { address: reference, documentKey, hit: null };
}

/**
 * The document whose key `written` is, in full or in a short form its corpus declares
 * (TR-B4-03; Johannes: "switch seemless between 10300058 and 3/58"), among the documents
 * searched; `null` if none has that key. The server finds a key whatever its syntax, and refuses
 * a query's syntax only when no document has it as a key, so that refusal is a key miss here
 * (Sol N1: `Letter~1#head` in light mode). A query too long to look up (`maximum_characters`)
 * proves nothing, so it fails (Sol N2).
 */
async function findDocumentByKey(projectId: string, written: string, searched: SearchedDocuments, signal?: AbortSignal): Promise<CorpusSearchHit | null> {
  try {
    const result = await searchCorpora(projectId, { query: written, scope: searched.scope, searchIn: WHOLE_TEXT, attached: searched.attached, limit: 1 }, signal);
    const [first] = result.hits;
    return first?.byKey === true ? first : null;
  } catch (caught) {
    const error = toUiError(caught);
    const refusesSyntax = isProblemWithCode(error, "invalid_value") && error.kind === "problem"
      && error.problem.details["field"] === "query" && !("maximum_characters" in error.problem.details);
    if (refusesSyntax) return null;
    throw caught;
  }
}

/** The key of the document a reference names, at its version or the latest; `null` if no document is there. */
async function readDocumentKey(document: DocumentAddress, signal?: AbortSignal): Promise<string | null> {
  try {
    const version = document.version ?? (await findLatestDocumentVersion(document.documentId, signal)).version;
    return (await readSourceDocument({ documentId: document.documentId, version }, signal)).key;
  } catch (caught) {
    const error = toUiError(caught);
    if (isProblemWithCode(error, "resource_not_found") || isProblemWithCode(error, "invalid_value")) return null;
    throw caught;
  }
}
